/**
 * pastoralJudgmentCoordinator.js
 *
 * Master coordinator for the Pastoral Reasoning + Spiritual Judgment layer.
 * Combines all 7 judgment modules into ONE compact system prompt block.
 *
 * Answers: "What kind of reasoning and spiritual judgment should Kevia
 * exercise right now to navigate this situation without oversimplifying it?"
 *
 * Output: ONE [JUICIO PASTORAL] block appended to the system prompt.
 * Signal-gated: omitted when complexity=simple AND no tension AND no conscience sensitivity.
 *
 * Design:
 *  — Pure function; no async, no side effects, no localStorage
 *  — Consumes assessment.emotion + assessment.ethics (no duplicate detection)
 *  — Consumes discernment.timing + discernment.depth (no duplication with FASE 6)
 *  — Consumes formation.presence (no duplication with FASE 7)
 *  — All 7 modules run; only non-null instructions are injected
 */

import { detectPastoralTension }       from "./pastoralTensionDetector";
import { resolveSpiritualComplexity }   from "./spiritualComplexityResolver";
import { prioritizePastoralPrinciple }  from "./principlePrioritizationEngine";
import { assessConscienceSensitivity }  from "./conscienceSensitivityEngine";
import { resolveRestorationVsCorrection } from "./restorationVsCorrectionEngine";
import { assessAmbiguityTolerance }     from "./ambiguityToleranceEngine";
import { discernSpiritualProcess }      from "./spiritualProcessDiscernment";

const MAX_JUDGMENT_CHARS = 500;

/**
 * @param {{
 *   text:                string,
 *   intent:              string,
 *   emotion:             object|null,   — from assessment.emotion
 *   mood:                string,
 *   riskLevel:           string,
 *   ethics:              object,        — from assessment.ethics
 *   discernmentTiming:   string,        — from discernment.timing
 *   discernmentDepth:    string,        — from discernment.depth
 *   formationPresence:   string,        — from formation.presence
 *   conversationLength:  number,
 * }} params
 * @returns {{
 *   judgmentBlock:  string|null,
 *   complexity:     string,
 *   tension:        object,
 *   principle:      string,
 *   ambiguityLevel: string,
 *   processState:   string,
 *   isActive:       boolean,
 * }}
 */
export function buildJudgmentContext({
  text,
  intent,
  emotion,
  mood               = "neutral",
  riskLevel          = "none",
  ethics             = {},
  discernmentTiming  = "respond_now",
  discernmentDepth   = "medium",
  formationPresence  = "acompañante",
  conversationLength = 0,
}) {
  // ── Run all 7 modules ────────────────────────────────────────────
  const tensionResult     = detectPastoralTension({ ethics, emotion, intent, riskLevel, text });
  const complexityResult  = resolveSpiritualComplexity({
    emotion, ethics, intent, riskLevel, conversationLength,
    hasTension: tensionResult.hasTension,
  });
  const principleResult   = prioritizePastoralPrinciple({
    ethics, emotion, riskLevel, intent,
    hasTension: tensionResult.hasTension,
    tension:    tensionResult.primary,
  });
  const conscienceResult  = assessConscienceSensitivity({ text, emotion, ethics, intent });
  const restorationResult = resolveRestorationVsCorrection({ ethics, emotion, riskLevel, intent, text });
  const ambiguityResult   = assessAmbiguityTolerance({
    complexity: complexityResult.complexity,
    hasTension: tensionResult.hasTension,
    emotion, riskLevel, intent,
  });
  const processResult     = discernSpiritualProcess({ text, emotion, intent });

  // ── Signal gate — skip if no complexity, no tension, no conscience ──
  const isActive =
    complexityResult.complexity !== "simple" ||
    tensionResult.hasTension ||
    conscienceResult.isActive;

  if (!isActive) {
    return {
      judgmentBlock:  null,
      complexity:     complexityResult.complexity,
      tension:        tensionResult,
      principle:      principleResult.principle,
      ambiguityLevel: ambiguityResult.toleranceLevel,
      processState:   processResult.state,
      isActive:       false,
    };
  }

  // ── Collect non-null instruction lines (priority order) ──────────
  const lines = [];

  // 1. Complexity framing first — sets the epistemic posture
  if (complexityResult.complexity === "deeply_complex") {
    lines.push("Esta situación es pastoralmente compleja. Procede con discernimiento, no con fórmulas.");
  } else if (complexityResult.complexity === "delicate") {
    lines.push("Situación delicada. Pisa con suavidad.");
  }

  // 2. Tension acknowledgment
  if (tensionResult.instruction) lines.push(tensionResult.instruction);

  // 3. Dominant principle
  if (principleResult.instruction) lines.push(principleResult.instruction);

  // 4. Conscience sensitivity
  if (conscienceResult.instruction) lines.push(conscienceResult.instruction);

  // 5. Restoration vs correction
  if (restorationResult.instruction) lines.push(restorationResult.instruction);

  // 6. Ambiguity tolerance
  if (ambiguityResult.instruction) lines.push(ambiguityResult.instruction);

  // 7. Spiritual process state
  if (processResult.instruction) lines.push(processResult.instruction);

  const judgmentBlock = lines.length > 0
    ? lines.join("\n").slice(0, MAX_JUDGMENT_CHARS)
    : null;

  return {
    judgmentBlock,
    complexity:     complexityResult.complexity,
    tension:        tensionResult,
    principle:      principleResult.principle,
    ambiguityLevel: ambiguityResult.toleranceLevel,
    processState:   processResult.state,
    isActive:       true,
  };
}
